import { DocumentData } from "firebase/firestore";

export const QUIZ_COLLECTION = "quizz_gl";
// Intentos por debajo de este ritmo se marcan para revision.
export const FAST_SECONDS_PER_QUESTION = 15;
const INTERNAL_DOMAIN = "@goodlook.com.mx";
const OPTION_LETTERS = ["A", "B", "C", "D"];
// Intentos previos a la gestion de cuestionarios: no traen quizId.
export const LEGACY_QUIZ_ID = "legacy";

export interface QuizAnswer {
    questionId: string;
    order: number;
    question: string;
    answer: string;
}

export interface QuizAttempt {
    id: string;
    quizId: string;
    name: string;
    email: string;
    grade: string;
    correctCount: number;
    totalQuestions: number;
    durationSeconds: number;
    startedAt: Date | null;
    finishedAt: Date | null;
    createdAt: Date | null;
    answers: QuizAnswer[];
    isInternal: boolean;
    isIncomplete: boolean;
}

export interface QuestionStat {
    questionId: string;
    order: number;
    question: string;
    key: string | null;
    answered: number;
    correct: number;
    accuracy: number | null;
    letters: Record<string, number>;
}

export interface QuizStats {
    attempts: QuizAttempt[];
    internalCount: number;
    externalCount: number;
    avgAccuracy: number;
    avgDuration: number;
    grades: { grade: string; count: number }[];
    questions: QuestionStat[];
    hasKey: boolean;
    keyDistribution: { letter: string; count: number }[];
    identicalAnswers: boolean;
    fastAttempts: QuizAttempt[];
    incompleteAttempts: QuizAttempt[];
}

const toDate = (value: any): Date | null => {
    if (!value) return null;
    if (typeof value.toDate === "function") return value.toDate();
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const normalizeAttempt = (id: string, data: DocumentData): QuizAttempt => {
    const answers: QuizAnswer[] = (Array.isArray(data.answers) ? data.answers : [])
        .map((a: any, index: number) => ({
            questionId: String(a?.questionId ?? index + 1),
            order: Number(a?.order ?? a?.questionId) || index + 1,
            question: String(a?.question ?? ""),
            answer: String(a?.answer ?? "").trim().toUpperCase()
        }));
    const startedAt = toDate(data.startedAt);
    const finishedAt = toDate(data.finishedAt);
    const email = String(data.email ?? id);
    const totalQuestions = Number(data.totalQuestions) || answers.length;
    const durationSeconds = Number(data.durationSeconds)
        || (startedAt && finishedAt ? Math.round((finishedAt.getTime() - startedAt.getTime()) / 1000) : 0);

    return {
        id,
        quizId: String(data.quizId ?? LEGACY_QUIZ_ID),
        name: String(data.name ?? email),
        email,
        grade: String(data.grade ?? "Sin calificación"),
        correctCount: Number(data.correctCount) || 0,
        totalQuestions,
        durationSeconds,
        startedAt,
        finishedAt,
        createdAt: toDate(data.createdAt) ?? finishedAt,
        answers,
        isInternal: email.toLowerCase().endsWith(INTERNAL_DOMAIN),
        isIncomplete: !data.name || !startedAt || !finishedAt || answers.length < totalQuestions
    };
};

// Los intentos historicos no tienen clave guardada: se infiere de los intentos con calificacion perfecta.
const inferAnswerKey = (attempts: QuizAttempt[]): Map<string, string> | null => {
    const perfect = attempts.find((a) => a.totalQuestions > 0 && a.correctCount === a.totalQuestions && a.answers.length === a.totalQuestions);
    if (!perfect) return null;
    return new Map(perfect.answers.map((a) => [a.questionId, a.answer]));
};

export const buildStats = (attempts: QuizAttempt[], answerKey?: Record<string, string> | null): QuizStats => {
    const key = answerKey ? new Map(Object.entries(answerKey)) : inferAnswerKey(attempts);
    const totalCorrect = attempts.reduce((acc, a) => acc + a.correctCount, 0);
    const totalQuestions = attempts.reduce((acc, a) => acc + a.totalQuestions, 0);

    const gradeCounts = new Map<string, number>();
    attempts.forEach((a) => gradeCounts.set(a.grade, (gradeCounts.get(a.grade) || 0) + 1));

    const questionMap = new Map<string, QuestionStat>();
    attempts.forEach((attempt) => attempt.answers.forEach((a) => {
        const stat = questionMap.get(a.questionId) ?? {
            questionId: a.questionId,
            order: a.order,
            question: a.question,
            key: key?.get(a.questionId) ?? null,
            answered: 0,
            correct: 0,
            accuracy: null,
            letters: {}
        };
        stat.answered += 1;
        stat.letters[a.answer] = (stat.letters[a.answer] || 0) + 1;
        if (stat.key && stat.key === a.answer) stat.correct += 1;
        questionMap.set(a.questionId, stat);
    }));
    const questions = Array.from(questionMap.values())
        .sort((a, b) => a.order - b.order)
        .map((q) => ({ ...q, accuracy: q.key ? q.correct / q.answered : null }));

    const letters = new Set(OPTION_LETTERS);
    questions.forEach((q) => q.key && letters.add(q.key));
    const keyDistribution = Array.from(letters).sort().map((letter) => ({
        letter,
        count: questions.filter((q) => q.key === letter).length
    }));

    const signature = (a: QuizAttempt) => [...a.answers].sort((x, y) => x.order - y.order).map((x) => x.answer).join("");
    const identicalAnswers = attempts.length > 1 && new Set(attempts.map(signature)).size === 1;

    return {
        attempts,
        internalCount: attempts.filter((a) => a.isInternal).length,
        externalCount: attempts.filter((a) => !a.isInternal).length,
        avgAccuracy: totalQuestions ? totalCorrect / totalQuestions : 0,
        avgDuration: attempts.length ? Math.round(attempts.reduce((acc, a) => acc + a.durationSeconds, 0) / attempts.length) : 0,
        grades: Array.from(gradeCounts.entries()).map(([grade, count]) => ({ grade, count })).sort((a, b) => b.count - a.count),
        questions,
        hasKey: Boolean(key),
        keyDistribution,
        identicalAnswers,
        fastAttempts: attempts.filter((a) => a.totalQuestions && a.durationSeconds / a.totalQuestions < FAST_SECONDS_PER_QUESTION),
        incompleteAttempts: attempts.filter((a) => a.isIncomplete)
    };
};

export const formatDuration = (seconds: number) => `${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, "0")}`;
